// Der benannte Export, nicht der Standardexport: unter `moduleResolution: NodeNext`
// bindet ein Standardimport aus diesem CommonJS-Paket das gesamte Modulobjekt und
// nicht die Klasse.
import { Decimal as DecimalBase } from 'decimal.js';
/**
 * Der Dezimalrechner dieses Pakets.
 *
 * Bewusst ein Klon: die globale Konfiguration von `decimal.js` bleibt unangetastet,
 * damit andere Pakete im selben Prozess ihre eigenen Einstellungen behalten.
 *
 * - `precision: 34` — 34 signifikante Stellen entsprechen IEEE 754 decimal128.
 *   `decimal.js` rundet **jede** Rechenoperation auf diese Stellenzahl, `plus` und
 *   `minus` eingeschlossen. „Addition ist exakt" gilt deshalb nicht unbedingt,
 *   sondern nur, solange das Ergebnis in 34 signifikante Stellen passt. Bei
 *   Centgenauigkeit sind das Beträge unterhalb von 10^32 Euro — eine Grenze, die im
 *   Rechnungswesen nie erreicht wird, die aber eine Bedingung bleibt und keine
 *   Eigenschaft. Bei Multiplikation und Division ist die Rundung dagegen der
 *   Regelfall und nicht der Grenzfall.
 * - `rounding: ROUND_HALF_UP` — kaufmännische Rundung, wie sie das deutsche
 *   Steuerrecht für Beträge erwartet.
 * - `toExpNeg`/`toExpPos` — die Standardwerte (-7 und 21) lassen `toString()` in
 *   Exponentialschreibweise umschlagen, was die kanonische Regel 1 bricht. Ein
 *   Beispiel: `div(money('0.01'), factor('1000000000'))` ergäbe sonst `1e-11`.
 *   Mit diesen Grenzen bleibt jede erreichbare Größenordnung in Dezimalnotation.
 */
export const D = DecimalBase.clone({
    precision: 34,
    rounding: DecimalBase.ROUND_HALF_UP,
    toExpNeg: -9e15,
    toExpPos: 9e15,
});
/** Geldbeträge tragen mindestens zwei Nachkommastellen (kanonische Regel 5). */
export const MONEY_MIN_SCALE = 2;
/** Faktoren werden nicht aufgefüllt: eine Menge von einem Stück ist `"1"`. */
export const FACTOR_MIN_SCALE = 0;
/**
 * Prüft die Eingabeform: optionales Minus, Ziffern, optional Punkt und Ziffern.
 *
 * Bewusst eng. Kein `+`, kein `1e5`, kein `1,5`, kein `.5`, kein `1.`, keine
 * Leerzeichen. Was hier durchfällt, ist ein Fehler des Aufrufers und keine
 * Gelegenheit zum Raten.
 */
export function isCanonicalInput(value) {
    return /^-?\d+(\.\d+)?$/.test(value);
}
/**
 * Bringt einen Dezimalwert in die kanonische Darstellung (Spezifikation 4.3).
 *
 * 1. Dezimalnotation, nie exponentiell
 * 2. Vorzeichen nur bei negativen Werten
 * 3. Kein Tausendertrennzeichen, Dezimaltrennzeichen ist der Punkt
 * 4. Nachlaufende Nullen entfallen, danach
 * 5. Auffüllen auf mindestens `minScale` Nachkommastellen
 * 6. Negative Null wird zu positiver Null — `"-0.00"` entsteht nie
 *
 * Die Reihenfolge von 4 und 5 ist wesentlich: `"1.500"` und `"1.50"` sind derselbe
 * Betrag und müssen dieselbe Darstellung bekommen, sonst wären zwei gleiche Beträge
 * nicht gleich.
 */
export function canonical(d, minScale) {
    // Regel 6: negative Null ist Null. Der Vergleich erfasst -0 ebenso wie -0.000.
    const value = d.isZero() ? new D('0') : d;
    // Regel 1: `toFixed()` ohne Argument liefert immer Dezimalnotation, unabhängig
    // von jeder Exponentenschwelle.
    const fixed = value.toFixed();
    const separator = fixed.indexOf('.');
    const integerPart = separator === -1 ? fixed : fixed.slice(0, separator);
    const fractionPart = separator === -1 ? '' : fixed.slice(separator + 1);
    // Regel 4 vor Regel 5.
    const stripped = fractionPart.replace(/0+$/, '');
    const padded = stripped.padEnd(minScale, '0');
    return padded === '' ? integerPart : `${integerPart}.${padded}`;
}
/** Kanonisiert einen Dezimalwert als Geldbetrag. Der einzige Weg zur Marke `Money`. */
export function toMoney(d) {
    return canonical(d, MONEY_MIN_SCALE);
}
/** Kanonisiert einen Dezimalwert als Faktor. Der einzige Weg zur Marke `Factor`. */
export function toFactor(d) {
    return canonical(d, FACTOR_MIN_SCALE);
}
//# sourceMappingURL=decimal.js.map